import {
    useLayoutEffect,
    useEffect,
    useState,
    Fragment
} from "react";
import {
    ActivityIndicator,
    TouchableOpacity,
    Dimensions,
    View
} from "react-native";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitSnackBar,
    NCoreUIKitTheme,
    PageContainer,
    TextInput,
    Button,
    Text
} from "ncore-ui-kit";
import {
    useAppState
} from "@react-native-community/hooks";
import {
    useNavigation,
    useIsFocused,
    useRoute,
    type RouteProp
} from "@react-navigation/native";
import {
    useSafeAreaInsets
} from "react-native-safe-area-context";
import {
    useCameraPermission,
    useCameraDevice,
    Camera
} from "react-native-vision-camera";
import {
    useBarcodeScannerOutput
} from "react-native-vision-camera-barcode-scanner";
import type AppStackParamList from "../../../types/navigation/app";
import {
    ChevronLeft
} from "lucide-react-native";
import {
    CircleAlert
} from "lucide-react-native/icons";
import ThreeTServiceInherit from "../../../services/threeT/context";

const ScanQR = () => {
    const route = useRoute<RouteProp<AppStackParamList, "ScanQR">>();

    const {
        eventSubID,
        eventID
    } = route.params;

    const {
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        requestPermission,
        hasPermission
    } = useCameraPermission();

    const [
        isCameraActive,
        setIsCameraActive
    ] = useState<boolean>(true);

    const [
        isLoading,
        setIsLoading
    ] = useState<boolean>(false);

    const [
        isCheckingAutoJoin,
        setIsCheckingAutoJoin
    ] = useState<boolean>(true);

    const [
        code,
        setCode
    ] = useState<string>("");

    const barcodeOutput = useBarcodeScannerOutput({
        barcodeFormats: ["qr-code"],
        onBarcodeScanned: (barcodes) => {
            if(barcodes.length > 0 && barcodes[0]?.rawValue) {
                setCode(barcodes[0].rawValue);
            }
        },
        onError: (error) => {
            console.error(error);
        }
    });

    const device = useCameraDevice("back");
    const navigation = useNavigation();
    const inset = useSafeAreaInsets();
    const isFocused = useIsFocused();
    const appState = useAppState();

    const isCameraActuallyActive = isFocused && appState === "active" && isCameraActive && !isCheckingAutoJoin;

    useEffect(() => {
        const checkAutoJoin = async () => {
            try {
                await ThreeTServiceInherit.getAccessTokenWithoutCode({
                    threetSubID: eventSubID,
                    threetID: eventID
                });

                // @ts-expect-error: Navigation route for dynamic stack is not fully typed
                navigation.navigate("ThreeTTableInfo");
            } catch (error) {
                console.log("Not auto-joined:", error);
            } finally {
                setIsCheckingAutoJoin(false);
            }
        };

        checkAutoJoin();
    }, [
        eventSubID,
        eventID,
        navigation
    ]);

    console.log("ss", eventSubID,
        eventID);

    useEffect(() => {
        const checkPermission = async () => {
            if(!hasPermission) {
                await requestPermission();
            }
        };
        checkPermission();
    }, [
        hasPermission,
        requestPermission
    ]);

    useLayoutEffect(() => {
        navigation.setOptions({
            headerShown: true,
            header: () => <View
                style={[
                    stylesheet.headerContainer,
                    {
                        backgroundColor: colors.content.container.default,
                        paddingHorizontal: spaces.spacingMd,
                        paddingTop: inset.top
                    }
                ]}
            >
                <TouchableOpacity
                    style={[
                        stylesheet.backButton,
                        {
                            backgroundColor: colors.content.container.subtle,
                            borderRadius: radiuses.full,
                            padding: spaces.spacingSm
                        }
                    ]}
                    onPress={handleGoBack}
                >
                    <ChevronLeft
                        color={colors.content.text.high}
                        size={30}
                    />
                </TouchableOpacity>
                <View
                    pointerEvents="none"
                    style={[
                        stylesheet.headerTitleContainer,
                        {
                            top: inset.top + spaces.spacingSm
                        }
                    ]}
                >
                    <Text
                        variant="titleLargeSize"
                        color="high"
                    >
                        {localize("scanQRCode")}
                    </Text>
                </View>
            </View>
        });
    }, [
        colors.content.container.default,
        colors.content.text.high,
        navigation,
        localize,
        spaces,
        inset
    ]);

    const handleGoBack = () => {
        setIsCameraActive(false);
        setTimeout(() => {
            if(navigation.canGoBack()) {
                navigation.goBack();
            }
        }, 150);
    };

    const handleSubmit = async () => {
        if(!code) {
            NCoreUIKitSnackBar.open({
                title: localize("error"),
                subTitle: localize("emptyRequestError"),
                type: "danger",
                isShowAction: false,
                autoCloseDelay: 5000,
                icon: ({
                    color
                }) => <CircleAlert
                    color={colors.content.icon[color ? color : "default"]}
                />
            });
            return;
        }

        setIsLoading(true);

        try {
            await ThreeTServiceInherit.getAccessToken({
                threetSubID: eventSubID,
                threetID: eventID,
                eventCode: code
            });

            // @ts-expect-error - navigation issue for dynamic paths
            navigation.navigate("ThreeTTableInfo");
        } catch (err: unknown) {
            console.log(err);
            NCoreUIKitSnackBar.open({
                title: localize("error"),
                subTitle: (err as Error).message || localize("error"),
                type: "danger",
                isShowAction: false,
                autoCloseDelay: 5000,
                icon: ({
                    color
                }) => <CircleAlert
                    color={colors.content.icon[color ? color : "default"]}
                />
            });
        } finally {
            setIsLoading(false);
        }
    };

    const renderScannerOverlay = () => {
        return <Fragment>
            <View
                style={stylesheet.cornerBottomRight}
            />
            <View
                style={stylesheet.cornerBottomLeft}
            />
            <View
                style={stylesheet.cornerTopRight}
            />
            <View
                style={stylesheet.cornerTopLeft}
            />
        </Fragment>;
    };

    const renderCamera = () => {
        if(isCheckingAutoJoin) {
            return <View
                style={[
                    stylesheet.cameraContainer,
                    stylesheet.loadingContainer,
                    {
                        backgroundColor: colors.content.container.default,
                        width: Dimensions.get("window").width - (spaces.spacingXl * 2),
                        borderRadius: radiuses.xl,
                        marginBottom: spaces.spacingLg
                    }
                ]}
            >
                <ActivityIndicator
                    size="large"
                />
            </View>;
        }

        if(!hasPermission || !device) {
            return <View
                style={[
                    stylesheet.cameraContainer,
                    {
                        backgroundColor: colors.content.container.default,
                        width: Dimensions.get("window").width - (spaces.spacingXl * 2),
                        borderRadius: radiuses.xl,
                        marginBottom: spaces.spacingLg
                    }
                ]}
            >
                {renderScannerOverlay()}
            </View>;
        }

        return <View
            style={[
                stylesheet.cameraContainer,
                {
                    backgroundColor: colors.content.container.default,
                    width: Dimensions.get("window").width - (spaces.spacingXl * 2),
                    borderRadius: radiuses.xl,
                    marginBottom: spaces.spacingLg
                }
            ]}
        >
            <Camera
                outputs={[
                    barcodeOutput
                ]}
                style={stylesheet.camera}
                isActive={isCameraActuallyActive}
                device={device}
            />
            {renderScannerOverlay()}
        </View>;
    };

    const renderSeparator = () => {
        return <View
            style={[
                stylesheet.separatorContainer,
                {
                    marginHorizontal: spaces.spacingXl,
                    marginBottom: spaces.spacingLg
                }
            ]}
        >
            <View
                style={[
                    stylesheet.separatorLine,
                    {
                        backgroundColor: colors.content.container.mid
                    }
                ]}
            />
            <Text
                style={[
                    stylesheet.separatorText,
                    {
                        marginHorizontal: spaces.spacingMd
                    }
                ]}
                customColor={colors.content.text.low}
                variant="bodySmallSize"
            >
                {localize("or")}
            </Text>
            <View
                style={[
                    stylesheet.separatorLine,
                    {
                        backgroundColor: colors.content.container.mid
                    }
                ]}
            />
        </View>;
    };

    const renderManualEntry = () => {
        return <View
            style={[
                stylesheet.manualEntryContainer,
                {
                    marginBottom: spaces.spacingLg
                }
            ]}
        >
            <TextInput
                placeholder={localize("manualEntryCodePlaceholder")}
                title={localize("manualEntryCodeTitle")}
                spreadBehaviour="stretch"
                onChangeText={setCode}
                value={code}
            />
        </View>;
    };

    return <PageContainer
        backgroundColor="default"
    >
        <View
            style={[
                stylesheet.container,
                {
                    backgroundColor: colors.content.container.default
                }
            ]}
        >
            <View
                style={{
                    ...stylesheet.contentContainer,
                    marginBottom: spaces.spacingLg
                }}
            >
                {renderCamera()}
                <Text
                    style={[
                        stylesheet.instructionText,
                        {
                            marginBottom: spaces.spacingLg
                        }
                    ]}
                    customColor={colors.content.text.low}
                    variant="bodyMediumSize"
                >
                    {localize("scanQRCodeInstruction")}
                </Text>
                {renderSeparator()}
                {renderManualEntry()}
            </View>
            <View
                style={[
                    stylesheet.footerContainer
                ]}
            >
                <Button
                    customTextColor={colors.project.brandWhite}
                    customColor={colors.project.black}
                    customBorderColor={colors.project.black}
                    spreadBehaviour="stretch"
                    isLoading={isLoading}
                    onPress={handleSubmit}
                    title={localize("submit")}
                    variant="filled"
                    size="large"
                />
            </View>
        </View>
    </PageContainer>;
};
export default ScanQR;
